import { useState } from "react";
import { Mail, Phone, MapPin, CheckCircle2, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import PageHero from "../components/PageHero";
import { SCHOOL } from "../lib/site";
import { submitContact } from "../lib/api";

export default function Contatti() {
  const [form, setForm] = useState({ nome: "", email: "", telefono: "", oggetto: "", messaggio: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      await submitContact(form);
      setDone(true);
      toast.success("Messaggio inviato!");
    } catch {
      toast.error("Errore. Verifica i dati e riprova.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="page-contatti">
      <PageHero
        eyebrow="Scrivici, chiamaci, passa a trovarci"
        title="Contatti, sedi"
        italic="e lezioni di prova."
        subtitle="Tre sedi tra le province di Ferrara e Rovigo. Siamo a disposizione per prenotare una lezione di prova gratuita o richiedere informazioni sui corsi e gli esami Trinity."
      />

      {/* Contatti rapidi */}
      <section className="py-20 px-6 md:px-12 lg:px-24 max-w-[1400px] mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#A3133A]/15 border border-[#A3133A]/15 mb-20">
          <div className="bg-[#FBFBF9] p-8" data-testid="contact-quick-email">
            <Mail size={20} className="text-[#A3133A] mb-3" />
            <div className="text-[10px] tracking-eyebrow text-[#5C5C5C] mb-2">Email</div>
            <a href={`mailto:${SCHOOL.email}`} className="font-serif-display text-2xl text-[#1C1C1C] link-underline break-all">
              {SCHOOL.email}
            </a>
          </div>
          <div className="bg-[#FBFBF9] p-8" data-testid="contact-quick-phones">
            <Phone size={20} className="text-[#A3133A] mb-3" />
            <div className="text-[10px] tracking-eyebrow text-[#5C5C5C] mb-3">Telefoni</div>
            <ul className="space-y-2">
              {SCHOOL.phones.map((p, i) => (
                <li key={p.value} className="text-sm">
                  <span className="text-[10px] tracking-eyebrow text-[#A3133A] block">{p.label}</span>
                  <a href={`tel:${p.tel}`} className="text-[#1C1C1C] link-underline">{p.value}</a>
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-[#A3133A] text-[#FBFBF9] p-8 relative grain overflow-hidden" data-testid="contact-quick-hours">
            <div className="relative z-10">
              <ExternalLink size={20} className="mb-3 opacity-90" />
              <div className="text-[10px] tracking-eyebrow opacity-80 mb-2">Segreteria didattica</div>
              <div className="font-serif-display text-xl">Lun — Ven · 15:00 — 20:00</div>
              <div className="font-serif-display text-xl">Sabato · 10:00 — 13:00</div>
            </div>
          </div>
        </div>
      </section>

      {/* Sedi */}
      <section className="px-6 md:px-12 lg:px-24 max-w-[1400px] mx-auto">
        <div className="text-[11px] tracking-eyebrow text-[#A3133A] mb-4">Le nostre sedi</div>
        <h2 className="font-serif-display text-4xl sm:text-5xl text-[#1C1C1C] leading-tight mb-12">
          Tre sedi tra<br />Ferrara e Rovigo.
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-24">
          {SCHOOL.locations.map((loc) => (
            <article
              key={loc.id}
              data-testid={`location-${loc.id}`}
              className="border border-[#A3133A]/15 bg-white flex flex-col"
            >
              <div className="aspect-[4/3] bg-[#A3133A]/5 overflow-hidden">
                <iframe
                  title={`Mappa ${loc.name}`}
                  src={`https://maps.google.com/maps?q=${encodeURIComponent(loc.mapsQuery)}&t=&z=15&ie=UTF8&iwloc=&output=embed`}
                  className="w-full h-full"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  data-testid={`location-map-${loc.id}`}
                />
              </div>
              <div className="p-7 flex-1 flex flex-col">
                <MapPin size={18} className="text-[#A3133A] mb-3" />
                <div className="text-[10px] tracking-eyebrow text-[#A3133A] mb-2">{loc.city}</div>
                <h3 className="font-serif-display text-2xl text-[#1C1C1C] mb-3 leading-tight">{loc.name}</h3>
                <p className="text-sm text-[#5C5C5C] flex-1">{loc.address}</p>
                <a
                  href={`https://maps.google.com/maps?q=${encodeURIComponent(loc.mapsQuery)}`}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`location-directions-${loc.id}`}
                  className="mt-5 text-[12px] tracking-eyebrow text-[#A3133A] link-underline inline-flex items-center gap-2"
                >
                  Indicazioni stradali <ExternalLink size={12} />
                </a>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* Form */}
      <section className="px-6 md:px-12 lg:px-24 max-w-[1100px] mx-auto pb-20">
        <div className="border border-[#A3133A]/15 bg-white p-8 sm:p-12">
          {done ? (
            <div data-testid="contact-success" className="text-center py-16">
              <CheckCircle2 size={56} className="text-[#A3133A] mx-auto mb-6" />
              <h3 className="font-serif-display text-3xl text-[#1C1C1C] mb-3">Messaggio inviato</h3>
              <p className="text-[#5C5C5C]">Ti risponderemo entro 48h all'indirizzo indicato.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
              <div>
                <div className="text-[11px] tracking-eyebrow text-[#A3133A] mb-2">Form di contatto</div>
                <h3 className="font-serif-display text-3xl text-[#1C1C1C]">Scrivici.</h3>
              </div>

              <div className="grid sm:grid-cols-2 gap-5">
                <Inp label="Nome e cognome" value={form.nome} onChange={(v) => update("nome", v)} testid="contact-nome" required />
                <Inp label="Email" type="email" value={form.email} onChange={(v) => update("email", v)} testid="contact-email-input" required />
              </div>
              <div className="grid sm:grid-cols-2 gap-5">
                <Inp label="Telefono" type="tel" value={form.telefono} onChange={(v) => update("telefono", v)} testid="contact-telefono" />
                <Inp label="Oggetto" value={form.oggetto} onChange={(v) => update("oggetto", v)} testid="contact-oggetto" required />
              </div>
              <div>
                <label className="block text-[11px] tracking-eyebrow text-[#5C5C5C] mb-2">Messaggio</label>
                <textarea
                  value={form.messaggio}
                  onChange={(e) => update("messaggio", e.target.value)}
                  data-testid="contact-messaggio"
                  required
                  rows={6}
                  className="w-full border border-[#A3133A]/30 rounded-sm px-4 py-3 bg-white focus:outline-none focus:border-[#A3133A]"
                />
              </div>
              <p className="text-[11px] text-[#5C5C5C]">
                Inviando il messaggio accetti la nostra informativa privacy ai sensi del GDPR 2016/679.
              </p>
              <button
                type="submit"
                disabled={loading}
                data-testid="contact-submit"
                className="bg-[#A3133A] text-white px-8 py-3 text-[12px] tracking-eyebrow rounded-sm hover:bg-[#8B0F2F] transition disabled:opacity-60"
              >
                {loading ? "Invio..." : "Invia messaggio"}
              </button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}

const Inp = ({ label, value, onChange, type = "text", testid, required }) => (
  <div>
    <label className="block text-[11px] tracking-eyebrow text-[#5C5C5C] mb-2">{label}</label>
    <input
      type={type}
      required={required}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
      className="w-full border border-[#A3133A]/30 rounded-sm px-4 py-3 bg-white focus:outline-none focus:border-[#A3133A]"
    />
  </div>
);
